import Seo from "@/components/Seo";
import { useEffect } from "react";
import { initScrollDepth, track } from "@/lib/analytics";
import Hero from "@/components/sections/Hero";
import MarqueeStrip from "@/components/sections/MarqueeStrip";
import ComparisonSection from "@/components/sections/ComparisonSection";
import ModuleSpotlight from "@/components/sections/ModuleSpotlight";
import PwaSection from "@/components/sections/PwaSection";
import IntegrationsSection from "@/components/sections/IntegrationsSection";
import BuiltFor from "@/components/sections/BuiltFor";
import TrustSecurity from "@/components/sections/TrustSecurity";
import PricingPreview from "@/components/sections/PricingPreview";
import FAQAccordion from "@/components/sections/FAQAccordion";
import FinalCTA from "@/components/sections/FinalCTA";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Stupeco WorkSpace",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "One Business. One Workspace. Connected Intelligence. Command Center to run today, a Board to help you think, CRM, Projects, Goals, Calendar and Business Data — all connected.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
};

export default function Home() {
  useEffect(() => {
    track("home_view");
    const cleanup = initScrollDepth();
    return cleanup;
  }, []);
  return (
    <>
      <Seo
        title="One Business. One Workspace. Connected Intelligence."
        description="Stupeco WorkSpace connects Command Center (run), Board (think), CRM, Projects, Goals, Calendar and Business Data — with shared business context across every module."
        path="/"
        jsonLd={jsonLd}
      />
      <Hero />
      <MarqueeStrip />

      {/* Problem → why WorkSpace exists */}
      <ComparisonSection />

      {/* Command Center — RUN */}
      <ModuleSpotlight
        testid="home-cc-spotlight"
        eyebrow="Command Center · Run"
        title="Start with what needs your attention."
        description="Command Center is the operational home of your business. Today's priorities, overdue work, follow-ups, upcoming commitments, pipeline health and goal progress — every signal that helps you act, in one place."
        bulletsTitle="What you see at a glance"
        bullets={[
          "Business Briefing with the day's crisp priorities",
          "Overdue tasks and upcoming deadlines",
          "Deals needing follow-up + pipeline pulse",
          "Primary goal + next milestone",
          "Commitments and recent activity",
        ]}
        screenshot="/product-shots/command-center-real.png"
        screenshotAlt="Command Center · workspace.stupeco.com"
        ctaLabel="See how Command Center works"
        ctaTo="/features#command-center"
      />

      {/* Board — THINK */}
      <ModuleSpotlight
        testid="home-board-spotlight"
        reverse
        accent="text-brand-blue"
        eyebrow="Board · Think"
        title="Don't make every business decision alone."
        description="WorkSpace ships with a virtual leadership team — CEO, CGO, CMO, CFO, COO, CTO, CHRO and a Chief of Staff — that examines your actual Workspace context from specialist perspectives. Ask questions, challenge assumptions, adopt decisions, and turn them into execution plans."
        bulletsTitle="What the Board does"
        bullets={[
          "Strategy, growth, marketing, finance, operations, tech, people",
          "Grounded in your real business data — not generic advice",
          "Asks for missing context instead of guessing",
          "Adopt a decision and Chief of Staff drafts the execution",
        ]}
        screenshot="/product-shots/board-real.png"
        screenshotAlt="Board · Leadership Team"
        ctaLabel="Meet your Leadership Team"
        ctaTo="/features#board"
      />

      {/* Sales / CRM */}
      <ModuleSpotlight
        testid="home-sales-spotlight"
        accent="text-brand-green"
        eyebrow="Sales · Grow"
        title="Keep revenue moving."
        description="Companies, contacts, deals, pipeline and follow-ups in one connected sales system. When a deal reaches Won, WorkSpace can draft the next-step plan for delivery and account follow-through."
        bulletsTitle="Sales, done in one place"
        bullets={[
          "Companies + contacts + deals share one context",
          "Follow-up nudges before deals stall",
          "Kanban pipeline with stage-by-stage clarity",
          "Won-deal → execution plan in one click",
        ]}
        screenshot="/product-shots/sales-real.png"
        screenshotAlt="Sales Command · Pipeline"
        ctaLabel="Explore Sales / CRM"
        ctaTo="/features#sales"
      />

      {/* Business Data */}
      <ModuleSpotlight
        testid="home-data-spotlight"
        reverse
        accent="text-brand-orange"
        eyebrow="Business Data · Adapt"
        title="Track what makes your business unique."
        description="Not every process belongs in CRM or Projects. Describe what you want to track — inventory, vendors, candidates, campaigns, channel partners, assets — and WorkSpace helps you structure the tracker in seconds."
        bulletsTitle="Where Business Data fits"
        bullets={[
          "Custom trackers for business-specific records",
          "Text, number, date, checkbox, single-select, URL, email",
          "Guardrails redirect you back to CRM when it belongs there",
          "Simple, focused CRUD — not another spreadsheet clone",
        ]}
        screenshot="/product-shots/data-real.png"
        screenshotAlt="Business Data · Custom Trackers"
        ctaLabel="See Business Data"
        ctaTo="/features#business-data"
      />

      {/* PWA / Wherever you work */}
      <PwaSection />

      <IntegrationsSection />
      <BuiltFor />
      <TrustSecurity />
      <PricingPreview />
      <FAQAccordion />
      <FinalCTA />
    </>
  );
}
